import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Badge, Spinner, Table } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminLogs.module.css";

interface EventItem {
  readonly id: string;
  readonly type: string;
  readonly stripeCustomerId: string | null;
  readonly orgId: string | null;
  readonly status: string;
  readonly receivedAt: string;
  readonly processedAt: string | null;
  readonly error: string | null;
  readonly attempts: number;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; events: readonly EventItem[] }
  | { kind: "error"; message: string };

const STATUS_OPTIONS = ["all", "pending", "processed", "failed", "ignored"] as const;

function eventVariant(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "processed") return "success";
  if (status === "pending") return "warning";
  if (status === "failed") return "danger";
  return "neutral";
}

export function AdminStripeEventsPage() {
  usePageTitle("Admin · Stripe events");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]>("all");
  const [type, setType] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (type.trim()) params.set("type", type.trim());
    fetch(`/api/admin/stripe-events?${params.toString()}`)
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: { events: EventItem[] } };
        if (json.success && json.data) setState({ kind: "ready", events: json.data.events });
        else setState({ kind: "error", message: "Unexpected response" });
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, [status, type]);

  if (state.kind === "unauthorized") return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;

  return (
    <AdminPage title="Stripe events">
      <div className={styles.filters}>
        <label className={styles.field}>
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as (typeof STATUS_OPTIONS)[number])}
            className={styles.input}
          >
            {STATUS_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
          </select>
        </label>
        <label className={styles.field}>
          Type
          <input
            type="text"
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="invoice.payment_failed"
            className={styles.input}
            style={{ minWidth: "14rem" }}
          />
        </label>
      </div>

      {state.kind === "loading" ? <Spinner />
      : state.kind === "error" ? <div className={styles.empty}>{state.message}</div>
      : state.events.length === 0 ? <div className={styles.empty}>No events.</div>
      : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Received</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Customer</Table.HeaderCell>
              <Table.HeaderCell>Org</Table.HeaderCell>
              <Table.HeaderCell>Attempts</Table.HeaderCell>
              <Table.HeaderCell>Error</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.events.map((e) => (
              <Table.Row key={e.id}>
                <Table.Cell>{new Date(e.receivedAt).toLocaleString()}</Table.Cell>
                <Table.Cell><span className={styles.mono}>{e.type}</span></Table.Cell>
                <Table.Cell><Badge variant={eventVariant(e.status)}>{e.status}</Badge></Table.Cell>
                <Table.Cell><span className={styles.mono}>{e.stripeCustomerId ?? "—"}</span></Table.Cell>
                <Table.Cell>
                  {e.orgId ? <Link to={`/admin/orgs/${e.orgId}`} className={styles.link}>{e.orgId.slice(0, 8)}…</Link> : "—"}
                </Table.Cell>
                <Table.Cell>{e.attempts}</Table.Cell>
                <Table.Cell>{e.error ?? ""}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </AdminPage>
  );
}
